import { BLUE_NOISE_128, BLUE_NOISE_SIZE } from './blue-noise-data';

/**
 * Sample the 128×128 blue-noise tile with bilinear interpolation.
 *
 * Returns the interpolated value in [0, 255]. The tile wraps at the
 * edges so any (fullX, fullY) is valid.
 *
 * At `scale === 1` and integer coordinates, the fractional offsets
 * are zero and the return value equals the raw tile entry - byte-
 * identical to a direct wrap-modulo lookup. This is the regression
 * baseline for callers that were previously hand-rolling the lookup.
 *
 * Larger `scale` enlarges the noise particles: the tile is sampled
 * at sub-tile coordinates `(fullX / scale, fullY / scale)` so each
 * tile cell now spans `scale × scale` output pixels, but the lerp
 * smooths the transition between neighbours so the result reads as
 * a soft blob rather than a block of identical pixels.
 *
 * Cost: 4 noise lookups + 3 lerps per pixel regardless of scale.
 */
export function sampleBlueNoiseBilinear(
  fullX: number,
  fullY: number,
  scale: number,
): number {
  const safeScale = scale > 0 ? scale : 1;
  const sx = fullX / safeScale;
  const sy = fullY / safeScale;
  const ix0 = Math.floor(sx);
  const iy0 = Math.floor(sy);
  const ix = ((ix0 % BLUE_NOISE_SIZE) + BLUE_NOISE_SIZE) % BLUE_NOISE_SIZE;
  const iy = ((iy0 % BLUE_NOISE_SIZE) + BLUE_NOISE_SIZE) % BLUE_NOISE_SIZE;
  const ixn = (ix + 1) % BLUE_NOISE_SIZE;
  const iyn = (iy + 1) % BLUE_NOISE_SIZE;
  const fx = sx - ix0;
  const fy = sy - iy0;
  const v00 = BLUE_NOISE_128[iy * BLUE_NOISE_SIZE + ix] ?? 0;
  const v10 = BLUE_NOISE_128[iy * BLUE_NOISE_SIZE + ixn] ?? 0;
  const v01 = BLUE_NOISE_128[iyn * BLUE_NOISE_SIZE + ix] ?? 0;
  const v11 = BLUE_NOISE_128[iyn * BLUE_NOISE_SIZE + ixn] ?? 0;
  const v0 = v00 + (v10 - v00) * fx;
  const v1 = v01 + (v11 - v01) * fx;
  return v0 + (v1 - v0) * fy;
}
